import * as React from "react";
import { useParams, useNavigate, Outlet } from "react-router-dom";
import { Button } from "@src/ui/views/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@src/ui/views/table";
import { Input } from "@src/ui/views/input";
import {
  useGetAllWelfareDefinitionsQuery,
  useCreateWelfareDefinitionMutation,
  useGenerateWelfareInstancesMutation,
  useDeleteWelfareDefinitionMutation,
} from "@src/features/welfare/redux/welfare-api";
import { Dialog, DialogContent, DialogTitle } from "@src/ui/views/dialog";
import type { WelfareDefinition } from "@src/features/welfare/welfare-models";
import { WelfareTypeContext } from "./welfare-layout";


export function WelfareDefinitionsScreen() {

  const { instanceType } = React.useContext(WelfareTypeContext);
  const { spaceId, definitionId } = useParams<{
    spaceId: string;
    definitionId?: string;
  }>();
  const navigate = useNavigate();

  // 2) load defs for that type
  const { data, isLoading, isError } = useGetAllWelfareDefinitionsQuery(
    { spaceId: spaceId!, instanceType },
    {
      // make sure your RTK hook signature supports instanceType
      skip: !spaceId,
    }
  );

  // other hooks…
  
  const [weeksAhead, setWeeksAhead] = React.useState(0);
  const [generateInstances, { isLoading: genLoading, isSuccess, isError: genError }] =
    useGenerateWelfareInstancesMutation();
  const [addOpen, setAddOpen] = React.useState(false);
  const [instanceName, setInstanceName] = React.useState("");
  const [maxParticipants, setMaxParticipants] = React.useState(0);
  const [descript, setDescript] = React.useState("");
  const [createDef, { isLoading: defLoading }] =
    useCreateWelfareDefinitionMutation();
  const [deleteDefinition, { isLoading: isDeletingDef }] =
    useDeleteWelfareDefinitionMutation();

  if (isLoading) return <p>Loading definitions…</p>;
  if (isError || !data) return <p>Error loading definitions</p>;

  const defs = data.data as WelfareDefinition[];

  const selectedDef = definitionId
    ? defs.find((d) => d.definitionId === definitionId)
    : undefined;

  return (
    <div className="p-6 space-y-8">
      {/* Page Title */}
      <h1 className="text-3xl font-bold">Welfare Definitions</h1>

      {/* Top Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-end gap-4">
          <label className="flex flex-col">
            <span className="text-sm font-medium">Weeks Ahead</span>
            <Input
              type="number"
              min={0}
              value={weeksAhead}
              onChange={(e) => setWeeksAhead(Number(e.target.value))}
              className="mt-1 w-20"
            />
          </label>
          <Button
            disabled={genLoading}
            onClick={() =>
              generateInstances({ spaceId: spaceId!, weeksAhead, instanceType })
            }
          >
            {genLoading ? "Generating…" : "Generate Instances"}
          </Button>
          {isSuccess && <span className="text-green-600">Scheduled!</span>}
          {genError && <span className="text-red-600">Error scheduling</span>}
        </div>
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          Add Definition
        </Button>
      </div>

      {/* Definitions List */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">Definitions</h2>
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {defs.map((def) => (
              <TableRow
                key={def.definitionId}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() =>
                  navigate(
                    `/dashboard/${spaceId}/welfare/definitions/${def.definitionId}`
                  )
                }
              >
                <TableCell>{def.instanceName}</TableCell>
                <TableCell>{def.descript}</TableCell>
                <TableCell className="space-x-2">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/dashboard/${spaceId}/welfare/definitions/${def.definitionId}`
                      );
                    }}
                  >
                    View Policies
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isDeletingDef}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await deleteDefinition({
                          spaceId: spaceId!,
                          definitionId: def.definitionId,
                        }).unwrap();
                      } catch (err: any) {
                        if (err.status === 409) {
                          alert(
                            "Please remove all occurrence policies first."
                          );
                        } else {
                          alert(`Error deleting: ${err.status}`);
                        }
                      }
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Occurrence Policies Container */}
      {selectedDef && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">
            Occurrence Policies for “{selectedDef.instanceName}”
          </h2>
          <Outlet />
        </div>
      )}

      {/* Add Definition Modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md space-y-4">
          <DialogTitle>Add New Definition</DialogTitle>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium">Name</label>
              <Input
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium">
                Max Participants
              </label>
              <Input
                type="number"
                min={1}
                value={maxParticipants}
                onChange={(e) =>
                  setMaxParticipants(Number(e.target.value))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Description</label>
              <Input
                value={descript}
                onChange={(e) => setDescript(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={defLoading}
              onClick={async () => {
                await createDef({
                  spaceId: spaceId!,
                  instanceName,
                  instanceType: instanceType,
                  maxParticipants,
                  descript,
                }).unwrap();
                setAddOpen(false);
                setInstanceName("");
                setMaxParticipants(0);
                setDescript("");
              }}
            >
              {defLoading ? "Adding…" : "Add Definition"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
